
import { PuttingResult } from "@/types/puttingResults";
import { SwingTendency, DistanceData } from "@/components/putting/training/charts/swing/types";

export const getExerciseType = (result: PuttingResult): string => {
  const testName = (result.test_name || '').toLowerCase();
  const testId = (result.test_id || '').toLowerCase();
  
  // Check both testId and testName - testId is more reliable as it's language-independent
  if (testId.includes('putt') || testName.includes('putt')) return 'Putting';
  if (testId.includes('chip') || testName.includes('chip')) return 'Chipping';
  if (testId.includes('wedg') || testName.includes('wedg')) return 'Wedging';
  if (testId.includes('bunker') || testName.includes('bunker')) return 'Bunker';
  if (testId.includes('driver') || testName.includes('driver')) return 'Driver';
  // Check for fairway wood BEFORE iron (since both might have similar patterns)
  if (testId.includes('fairwaywood') || testName.includes('fairwaywood') || 
      testName.includes('bois') || testName.includes('wood') || testName.includes('madera')) return 'BoisDeParcours';
  if (testId.includes('iron') || testName.includes('iron') || 
      testName.includes('fer') || testName.includes('hierro') ||
      testId.includes('attaque') || testName.includes('attaque') || 
      testName.includes('green attack') || testName.includes('ataque')) return 'Fers';
  
  return 'Autres';
};

export const groupResultsByExercise = (trainingResults: PuttingResult[]) => {
  const exerciseGroups: Record<string, PuttingResult[]> = {};
  
  trainingResults.forEach(result => {
    const exerciseType = getExerciseType(result);
    if (!exerciseGroups[exerciseType]) {
      exerciseGroups[exerciseType] = [];
    }
    exerciseGroups[exerciseType].push(result);
  });

  return exerciseGroups;
};

export const prepareExerciseData = (results: PuttingResult[], selectedLevel: string | null) => {
  const filteredResults = selectedLevel 
    ? results.filter(r => String(r.test_level) === selectedLevel)
    : results;

  return filteredResults
    .sort((a, b) => new Date(a.test_date).getTime() - new Date(b.test_date).getTime())
    .map(result => ({
      date: new Date(result.test_date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }),
      points: result.points,
      testName: result.test_name || result.test_id || 'Standard',
      testId: result.test_id || result.test_name || 'Standard', // Preserve original ID for color matching
      fullDate: result.test_date
    }));
};

export const calculateTendencyData = (
  exerciseType: string, 
  swingTendencies: SwingTendency[], 
  selectedLevel: string | null
): DistanceData => {
  console.log(`Calculating tendency data for exercise type: ${exerciseType}`);
  console.log(`Available swing tendencies:`, swingTendencies.length);
  console.log(`Selected level filter:`, selectedLevel);
  
  // Utiliser exactement la même logique que SwingDistanceCharts
  let filteredTendencies: SwingTendency[] = [];
  
  switch (exerciseType) {
    case 'Fers':
      filteredTendencies = swingTendencies.filter(t => t.club_type === 'iron');
      break;
    case 'BoisDeParcours':
      filteredTendencies = swingTendencies.filter(t => t.club_type === 'fairwayWood');
      break;
    case 'Driver':
      filteredTendencies = swingTendencies.filter(t => t.club_type === 'driver');
      break;
    default:
      filteredTendencies = [];
  }

  // Appliquer le filtre de niveau si spécifié
  if (selectedLevel !== null) {
    filteredTendencies = filteredTendencies.filter(t => 
      String(t.test_level) === selectedLevel
    );
  }

  console.log(`Filtered tendencies for ${exerciseType}:`, filteredTendencies.length);
  console.log(`Filtered tendencies detail:`, filteredTendencies.map(t => ({
    id: t.id,
    tendency: t.tendency,
    club_type: t.club_type,
    test_id: t.test_id,
    test_level: t.test_level,
    distance: t.distance
  })));

  let left = 0, center = 0, right = 0, total = 0, points = 0;

  filteredTendencies.forEach(t => {
    if (t.tendency === 'left') left++;
    else if (t.tendency === 'center') center++;
    else if (t.tendency === 'right') right++;
    total++;
    
    // Add points if available
    if (t.points) {
      points += t.points;
    }
  });

  const result = { left, center, right, total, points };
  console.log(`Tendency data result for ${exerciseType}:`, result);
  return result;
};

export const hasTendencies = (exerciseType: string): boolean => {
  return ['Fers', 'BoisDeParcours', 'Driver'].includes(exerciseType);
};

// Nouvelle fonction pour vérifier si un type d'exercice de swing devrait être affiché
export const shouldShowSwingExercise = (
  exerciseType: string, 
  swingTendencies: SwingTendency[], 
  selectedLevel: string | null
): boolean => {
  if (!hasTendencies(exerciseType)) return false;
  
  // Pour les exercices de swing, on les affiche s'il y a des tendances pour ce type de club
  // même si pas spécifiquement pour le niveau sélectionné
  let hasRelevantTendencies = false;
  
  switch (exerciseType) {
    case 'Fers':
      hasRelevantTendencies = swingTendencies.some(t => t.club_type === 'iron');
      break;
    case 'BoisDeParcours':
      hasRelevantTendencies = swingTendencies.some(t => t.club_type === 'fairwayWood');
      break;
    case 'Driver':
      hasRelevantTendencies = swingTendencies.some(t => t.club_type === 'driver');
      break;
  }
  
  return hasRelevantTendencies;
};
